/** Map lip-sync voice template API rows to voice dialog UI model. */

import {
  parseVoiceCloneParams,
  resolveVoiceCloneAfterUrl,
  resolveVoiceCloneBeforeUrl,
} from "~/constants/aiVoiceCloning.js";

const VOICE_GENDER_ORDER = ["Male", "Female"];
const VOICE_AGE_ORDER = ["Child", "Young", "Middle-Aged", "Older"];

const VOICE_SOURCE_MY_VOICE = "my_voice";
const VOICE_SOURCE_LIBRARY = "library";
export const LIP_SYNC_MY_VOICE_MODEL_TYPE = "qwen3_tts";

function titleCase(value, fallback = "") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(" ");
}

function normalizeVoiceCloneGender(value) {
  const text = String(value || "").trim().toLowerCase();
  if (text === "female") return "Female";
  if (text === "male") return "Male";
  return titleCase(text, "Female");
}

function normalizeVoiceCloneAge(value) {
  const text = String(value || "").trim().toLowerCase();
  if (text === "middle-aged" || text === "middle aged") return "Middle-Aged";
  if (text === "old" || text === "older") return "Older";
  if (text === "young") return "Young";
  if (text === "child" || text === "kid") return "Child";
  return titleCase(text, "Young");
}

function explicitSavedMarker(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return null;
  if (["1", "true", "yes", "saved", "collection", "collected"].includes(text)) return true;
  if (
    [
      "0",
      "false",
      "no",
      "unsaved",
      "not_saved",
      "not saved",
      "uncollected",
      "not_collected",
      "not collected",
    ].includes(text)
  ) {
    return false;
  }
  return null;
}

function explicitSavedName(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  return !["Cloned Voice", "Voice Clone Preview"].includes(text);
}

function numericStatus(value) {
  if (typeof value === "boolean") return null;
  if (value == null || value === "") return null;
  const status = Number(value);
  return Number.isFinite(status) ? status : null;
}

export function readVoiceCloneTaskStatus(raw = {}) {
  const params = parseVoiceCloneParams(raw?.params);
  const candidates = [
    raw?.task_status,
    raw?.taskStatus,
    raw?.generation_status,
    raw?.generationStatus,
    raw?.status,
    params?.task_status,
    params?.taskStatus,
    params?.generation_status,
    params?.generationStatus,
    params?.status,
  ];
  for (const candidate of candidates) {
    const status = numericStatus(candidate);
    if (status != null) return status;
  }
  return null;
}

export function resolveVoiceCloneTaskStatus(raw = {}, options = {}) {
  const status = readVoiceCloneTaskStatus(raw);
  if (status != null) return status;
  if (Number.isFinite(Number(options.defaultStatus))) return Number(options.defaultStatus);
  return options.pending ? 0 : 1;
}

export function readVoiceCloneSavedStatus(raw = {}) {
  const params = parseVoiceCloneParams(raw?.params);
  if (typeof raw?.status === "boolean") return raw.status;
  if (typeof params?.status === "boolean") return params.status;

  const markers = [
    raw?.is_saved,
    raw?.saved,
    raw?.voice_saved,
    raw?.save_status,
    raw?.is_collection,
    raw?.isCollection,
    raw?.collection,
    params?.is_saved,
    params?.saved,
    params?.voice_saved,
    params?.save_status,
    params?.is_collection,
    params?.isCollection,
    params?.collection,
  ];
  for (const marker of markers) {
    const saved = explicitSavedMarker(marker);
    if (saved != null) return saved;
  }
  return null;
}

function normalizeVoiceCloneVoiceId(raw = {}, params = {}) {
  return String(
    raw?.voice_id ||
      raw?.voice_type ||
      raw?.id ||
      raw?.task_id ||
      raw?.taskId ||
      params?.voice_id ||
      params?.voice_type ||
      params?.id ||
      params?.task_id ||
      params?.taskId ||
      "",
  ).trim();
}

function normalizeVoiceCloneName(raw = {}, params = {}, fallback = "Cloned Voice") {
  return String(
    raw?.voice_name ||
      raw?.name ||
      raw?.voice_label ||
      params?.voice_name ||
      params?.name ||
      params?.voice_label ||
      fallback,
  ).trim();
}

function collectUniqueFacetValues(voices, field, preferredOrder = []) {
  const seen = new Set();
  for (const voice of voices || []) {
    const value = voice?.[field];
    if (value) seen.add(String(value));
  }
  const ordered = preferredOrder.filter((item) => seen.has(item));
  const rest = [...seen]
    .filter((item) => !preferredOrder.includes(item))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
}

/** Unique filter option values present in the loaded voice catalog. */
export function buildVoiceFilterFacets(voices) {
  return {
    languages: collectUniqueFacetValues(voices, "language"),
    genders: collectUniqueFacetValues(voices, "gender", VOICE_GENDER_ORDER),
    ages: collectUniqueFacetValues(voices, "age", VOICE_AGE_ORDER),
  };
}

export function mapLipSyncTemplateToVoice(item) {
  const voiceId = String(item?.voice_id ?? item?.voice_type ?? "").trim();
  const source = item?.source || (item?.is_voice_clone || item?.isClonedVoice ? VOICE_SOURCE_MY_VOICE : VOICE_SOURCE_LIBRARY);
  return {
    templateId: item?.template_id ?? item?.templateId,
    template_id: item?.template_id ?? item?.templateId,
    voice_id: voiceId,
    voice_type: voiceId,
    name: item?.name ?? voiceId,
    gender: item?.gender ?? "Male",
    age: item?.age ?? "Middle-Aged",
    language: item?.language ?? "English",
    audio_url: item?.audio_url ?? "",
    isCollection: Boolean(item?.is_collection ?? item?.isCollection),
    isClonedVoice: Boolean(item?.is_voice_clone || item?.isClonedVoice || source === VOICE_SOURCE_MY_VOICE),
    source,
    description: "",
    emotions: ["Neutral"],
    selectedEmotion: "Neutral",
  };
}

export function mapLipSyncTemplatesToVoices(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapLipSyncTemplateToVoice).filter((v) => v.voice_id);
}

export function collectSavedVoiceIds(voices) {
  return (voices || [])
    .filter((v) => v.isCollection)
    .map((v) => v.voice_id)
    .filter(Boolean);
}

export function buildVoiceCatalog(apiList) {
  const voices = mapLipSyncTemplatesToVoices(apiList);
  return {
    voices,
    savedIds: collectSavedVoiceIds(voices),
  };
}

export function resolveVoiceSource(voice) {
  return voice?.source === VOICE_SOURCE_MY_VOICE || voice?.isClonedVoice
    ? VOICE_SOURCE_MY_VOICE
    : VOICE_SOURCE_LIBRARY;
}

export function isMyVoice(voice) {
  return resolveVoiceSource(voice) === VOICE_SOURCE_MY_VOICE;
}

export function isSavedVoiceCloneHistoryItem(raw) {
  const params = parseVoiceCloneParams(raw?.params);
  const afterAudioUrl = resolveVoiceCloneAfterUrl({ ...raw, params });
  if (!afterAudioUrl) return false;
  const taskStatus = resolveVoiceCloneTaskStatus(raw);
  if (taskStatus === 0 || taskStatus === 2) return false;

  const savedStatus = readVoiceCloneSavedStatus(raw);
  if (savedStatus != null) return savedStatus;

  return Boolean(
    raw?.voice_name ||
      params?.voice_name ||
      explicitSavedName(raw?.name) ||
      explicitSavedName(params?.name),
  );
}

export function mapVoiceCloneHistoryItemToVoice(raw) {
  if (!isSavedVoiceCloneHistoryItem(raw)) return null;
  const params = parseVoiceCloneParams(raw?.params);
  const voiceId = normalizeVoiceCloneVoiceId(raw, params);
  const afterAudioUrl = resolveVoiceCloneAfterUrl({ ...raw, params });
  if (!voiceId || !afterAudioUrl) return null;
  const beforeAudioUrl = resolveVoiceCloneBeforeUrl({ ...raw, params });
  const name = normalizeVoiceCloneName(raw, params);
  return {
    templateId: raw?.template_id ?? raw?.templateId ?? null,
    template_id: raw?.template_id ?? raw?.templateId ?? null,
    voice_id: voiceId,
    voice_type: voiceId,
    name: name || voiceId,
    voice_name: name || voiceId,
    gender: normalizeVoiceCloneGender(raw?.gender || params?.gender),
    age: normalizeVoiceCloneAge(raw?.age || params?.age),
    language: titleCase(raw?.language || params?.language || params?.preview_language, "English"),
    audio_url: afterAudioUrl,
    before_audio_url: beforeAudioUrl,
    after_audio_url: afterAudioUrl,
    description: raw?.description || params?.description || "",
    isCollection: true,
    isClonedVoice: true,
    source: VOICE_SOURCE_MY_VOICE,
    task_id: raw?.task_id || raw?.taskId || "",
    emotions: ["Neutral"],
    selectedEmotion: "Neutral",
  };
}

export function mapVoiceCloneHistoryToVoices(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const voices = [];
  for (const item of list) {
    const voice = mapVoiceCloneHistoryItemToVoice(item);
    if (!voice) continue;
    const key = voice.voice_id || voice.voice_type;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    voices.push(voice);
  }
  return voices;
}

export function mapUserVoiceItemToVoice(raw) {
  const params = parseVoiceCloneParams(raw?.params);
  const voiceId = normalizeVoiceCloneVoiceId(raw, params);
  const afterAudioUrl = resolveVoiceCloneAfterUrl({ ...raw, params });
  if (!voiceId || !afterAudioUrl) return null;
  const beforeAudioUrl = resolveVoiceCloneBeforeUrl({ ...raw, params });
  const name = normalizeVoiceCloneName(raw, params, voiceId);
  return {
    templateId: raw?.template_id ?? raw?.templateId ?? null,
    template_id: raw?.template_id ?? raw?.templateId ?? null,
    voice_id: voiceId,
    voice_type: voiceId,
    name: name || voiceId,
    voice_name: name || voiceId,
    gender: normalizeVoiceCloneGender(raw?.gender || params?.gender),
    age: normalizeVoiceCloneAge(raw?.age || params?.age),
    language: titleCase(raw?.language || params?.language || params?.preview_language, "English"),
    voice_url: afterAudioUrl,
    audio_url: afterAudioUrl,
    before_audio_url: beforeAudioUrl,
    after_audio_url: afterAudioUrl,
    description: raw?.description || params?.description || "",
    isCollection: true,
    isClonedVoice: true,
    source: VOICE_SOURCE_MY_VOICE,
    task_id: raw?.task_id || raw?.taskId || "",
    emotions: ["Neutral"],
    selectedEmotion: "Neutral",
  };
}

export function buildMyVoiceTaskParams(voice) {
  if (!isMyVoice(voice)) return {};
  const voiceId = String(voice?.voice_id ?? voice?.voice_type ?? "").trim();
  const audioUrl = String(voice?.voice_url || voice?.audio_url || voice?.after_audio_url || "").trim();
  if (!voiceId || !audioUrl) return {};
  return {
    voice_id: voiceId,
    model_type: LIP_SYNC_MY_VOICE_MODEL_TYPE,
    audio_url: audioUrl,
  };
}

export function mapUserVoiceListToVoices(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const voices = [];
  for (const item of list) {
    const voice = mapUserVoiceItemToVoice(item);
    if (!voice) continue;
    const key = voice.voice_id || voice.voice_type;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    voices.push(voice);
  }
  return voices;
}

export function mergeVoiceCatalogWithMyVoices(libraryVoices, myVoices) {
  const myVoiceKeys = new Set((myVoices || []).map((voice) => voice.voice_id || voice.voice_type).filter(Boolean));
  const library = (libraryVoices || [])
    .filter((voice) => {
      const key = voice.voice_id || voice.voice_type;
      return key && !myVoiceKeys.has(key);
    })
    .map((voice) => ({
      ...voice,
      source: resolveVoiceSource(voice),
    }));
  return [...(myVoices || []), ...library];
}

export function buildVoiceCatalogWithMyVoices(apiList, userVoiceList, fallbackVoices = []) {
  const catalog = buildVoiceCatalog(apiList);
  const myVoices = mapUserVoiceListToVoices(userVoiceList);
  const libraryVoices = catalog.voices.length ? catalog.voices : fallbackVoices;
  return {
    voices: mergeVoiceCatalogWithMyVoices(libraryVoices, myVoices),
    savedIds: catalog.savedIds,
    myVoiceIds: myVoices.map((voice) => voice.voice_id).filter(Boolean),
  };
}

export function setVoiceCollectionState(voices, voiceKey, isCollection) {
  const item = voices.find((v) => v.voice_id === voiceKey || v.voice_type === voiceKey);
  if (item) item.isCollection = isCollection;
}

/** First voice row from generated-templates (voice) — used to seed localStorage before catalog loads. */
export const LIP_SYNC_DEFAULT_VOICE_TEMPLATE = {
  age: "Middle-Aged",
  audio_url: "https://static.lip-sync.ai/lipsync/voice_demo/English_Trustworth_Man.mp3",
  gender: "Male",
  is_collection: false,
  language: "English",
  name: "Trustworth Man",
  template_id: 608,
  voice_id: "English_Trustworth_Man",
};

export const LIP_SYNC_SELECTED_VOICE_STORAGE_KEY = "lip_sync_selected_voice";

/** Default voice for AI Baby Podcast — `voice_id` resolved from catalog by name on load. */
export const AI_BABY_PODCAST_DEFAULT_VOICE_TEMPLATE = {
  name: "Cute Spirit",
  gender: "Female",
  age: "Child",
  language: "Chinese",
  is_collection: false,
  audio_url: "",
};

export const AI_BABY_PODCAST_SELECTED_VOICE_STORAGE_KEY =
  `${LIP_SYNC_SELECTED_VOICE_STORAGE_KEY}:ai-baby-podcast`;

/** Default voice for AI Influencer — `voice_id` resolved from catalog by name on load. */
export const AI_INFLUENCER_DEFAULT_VOICE_TEMPLATE = {
  name: "Stressed Lady",
  gender: "Female",
  age: "Middle-Aged",
  language: "English",
  is_collection: false,
  audio_url: "",
};

export const AI_INFLUENCER_SELECTED_VOICE_STORAGE_KEY =
  `${LIP_SYNC_SELECTED_VOICE_STORAGE_KEY}:ai-influencer`;

function voiceToStoragePayload(voice) {
  const voiceId = String(voice?.voice_id ?? voice?.voice_type ?? "").trim();
  const name = String(voice?.name ?? "").trim();
  if (!voiceId && !name) return null;
  const source = resolveVoiceSource(voice);
  return {
    template_id: voice.template_id ?? voice.templateId,
    voice_id: voiceId,
    name: name || voiceId,
    gender: voice.gender,
    age: voice.age,
    language: voice.language,
    audio_url: voice.audio_url ?? "",
    is_collection: Boolean(voice.isCollection ?? voice.is_collection),
    is_voice_clone: source === VOICE_SOURCE_MY_VOICE,
    source,
  };
}

export function findVoiceInCatalog(voices, criteria = {}) {
  const list = voices || [];
  const voiceId = String(criteria.voice_id ?? criteria.voice_type ?? "").trim();
  const name = String(criteria.name ?? "").trim();
  if (voiceId) {
    const byId = list.find(
      (voice) => voice.voice_id === voiceId || voice.voice_type === voiceId,
    );
    if (byId) return byId;
  }
  if (name) {
    return list.find((voice) => String(voice.name ?? "").trim() === name) ?? null;
  }
  return null;
}

/** Pick persisted / default / first voice after the voice catalog API loads. */
export function resolveVoiceAfterCatalogLoad(
  voices,
  { previousVoice, defaultVoiceTemplate } = {},
) {
  const list = voices || [];
  const previousKey =
    previousVoice?.voice_id || previousVoice?.voice_type || "";
  if (previousKey) {
    const prev = findVoiceInCatalog(list, { voice_id: previousKey });
    if (prev) return prev;
  }
  const previousName = String(previousVoice?.name ?? "").trim();
  if (previousName) {
    const byName = findVoiceInCatalog(list, { name: previousName });
    if (byName) return byName;
  }
  if (defaultVoiceTemplate) {
    const def = findVoiceInCatalog(list, defaultVoiceTemplate);
    if (def) return def;
  }
  return list[0] ?? null;
}

export function readStoredSelectedVoice(
  storageKey = LIP_SYNC_SELECTED_VOICE_STORAGE_KEY,
) {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const voice = mapLipSyncTemplateToVoice(parsed);
    return voice.voice_id || voice.name ? voice : null;
  } catch {
    return null;
  }
}

export function writeStoredSelectedVoice(
  voice,
  storageKey = LIP_SYNC_SELECTED_VOICE_STORAGE_KEY,
) {
  if (typeof localStorage === "undefined") return;
  const payload = voiceToStoragePayload(voice);
  if (!payload) return;
  localStorage.setItem(storageKey, JSON.stringify(payload));
}

/** Seed localStorage once, then return the voice used for page init. */
export function getInitialSelectedVoice(options) {
  const storageKey = options?.storageKey || LIP_SYNC_SELECTED_VOICE_STORAGE_KEY;
  const defaultTemplate =
    options?.defaultVoiceTemplate ?? LIP_SYNC_DEFAULT_VOICE_TEMPLATE;
  const stored = readStoredSelectedVoice(storageKey);
  if (stored) return stored;
  const seeded = mapLipSyncTemplateToVoice(defaultTemplate);
  writeStoredSelectedVoice(seeded, storageKey);
  return seeded;
}
